import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Descriptions, Button, Space, Tag, Typography, Popconfirm, Spin, message } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  StopOutlined,
  EditOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCustomerCreditNote, usePostCustomerCreditNote, useCancelCustomerCreditNote } from '@/hooks/useCustomerCreditNote';
import { useCurrency } from '@/contexts/CurrencyContext';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import complianceService from '@/services/compliance.service';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import InheritedAttachmentsCard from '@/components/documents/InheritedAttachmentsCard';
import {
  DOCUMENT_ENTITY_TYPE_CUSTOMER_CREDIT_NOTE,
  SCM_ATTACHMENT_UPLOAD_HINT,
} from '@/constants/document-entity-types';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

const { Title, Text } = Typography;

const CustomerCreditNoteDetailPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatCurrency } = useCurrency();
  const { data: cn, isLoading } = useCustomerCreditNote(id ?? null);
  const postMutation = usePostCustomerCreditNote();
  const cancelMutation = useCancelCustomerCreditNote();
  const [submitting, setSubmitting] = useState(false);

  const statusLabel = (status: string) => {
    const map: Record<string, string> = {
      Draft: t('statusDraft'),
      Posted: t('statusPosted'),
      Cancelled: t('statusCancelled'),
    };
    return map[status] ?? status;
  };

  const { data: transmissions = [] } = useQuery({
    queryKey: ['uae-einvoice-transmissions', 'CustomerCreditNote', id],
    queryFn: () =>
      id
        ? complianceService.listTransmissions({
            document_type: 'CustomerCreditNote',
            document_id: id,
          })
        : Promise.resolve([]),
    enabled: !!id,
  });

  const handleSubmitEinvoice = async () => {
    if (!id) return;
    setSubmitting(true);
    try {
      const result = await complianceService.generateUaeEInvoice(id, 'CustomerCreditNote');
      message.success(
        result.idempotentReplay
          ? t('alreadySubmitted', { status: result.status })
          : t('submittedToAsp', { status: result.status })
      );
      queryClient.invalidateQueries({
        queryKey: ['uae-einvoice-transmissions', 'CustomerCreditNote', id],
      });
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || t('eInvoiceSubmitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbSegments = useMemo(
    () =>
      cn
        ? [
            { title: t('financeModule'), path: '/fin/dashboard' },
            { title: t('creditNotes'), path: '/fin/credit-notes' },
            { title: cn.credit_note_number || t('creditNote') },
          ]
        : null,
    [cn?.id, cn?.credit_note_number, t]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const ccnWorkflowStages = useMemo(() => {
    if (!cn) return [];
    const cancelled = cn.status === 'Cancelled';
    const posted = cn.status === 'Posted';
    return [
      {
        title: t('statusDraft'),
        description: t('workflowEntry'),
        status: (cancelled ? 'error' : cn.status === 'Draft' ? 'process' : 'finish') as
          | 'wait'
          | 'process'
          | 'finish'
          | 'error',
      },
      {
        title: t('statusPosted'),
        description: t('workflowGl'),
        status: (cancelled ? 'wait' : posted ? 'finish' : 'wait') as
          | 'wait'
          | 'process'
          | 'finish'
          | 'error',
      },
    ];
  }, [cn, t]);

  const ccnWorkflowCurrent = useMemo(() => {
    if (!cn || cn.status === 'Cancelled') return 0;
    if (cn.status === 'Posted') return 1;
    return 0;
  }, [cn]);

  if (!id) {
    navigate('/fin/credit-notes');
    return null;
  }

  if (isLoading && !cn) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!cn) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Text type="secondary">{t('creditNoteNotFound')}</Text>
        </Card>
      </div>
    );
  }

  const isDraft = cn.status === 'Draft';
  const isPosted = cn.status === 'Posted';
  const invId = cn.original_sales_invoice_id;
  const latestTx = transmissions[0];

  const headerActions = (
    <Space wrap size="small">
      <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/fin/credit-notes')}>
        {tc('back')}
      </Button>
      <ReportDocumentActions
        reportKey="customer-credit-note"
        params={id ? { id } : null}
        documentId={id}
        sendPermissions={['finance.voucher.create']}
        emailLabel={t('email')}
      />
      {isDraft && (
        <Button type="default" icon={<EditOutlined />} onClick={() => navigate(`/fin/credit-notes/${id}/edit`)}>
          {tc('edit')}
        </Button>
      )}
      {isDraft && (
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          loading={postMutation.isPending}
          onClick={() => postMutation.mutate(id)}
        >
          {t('postToGl')}
        </Button>
      )}
      {isPosted && (
        <Button
          type="primary"
          icon={<FileProtectOutlined />}
          loading={submitting}
          onClick={handleSubmitEinvoice}
        >
          {t('submitUaeEInvoice')}
        </Button>
      )}
      {isPosted && (
        <Popconfirm
          title={t('cancelCreditNoteConfirm')}
          onConfirm={() => cancelMutation.mutate(id)}
          okText={tc('yes')}
          cancelText={tc('no')}
        >
          <Button danger icon={<StopOutlined />} loading={cancelMutation.isPending}>
            {tc('cancel')}
          </Button>
        </Popconfirm>
      )}
    </Space>
  );

  const rightRail = (
    <div>
      <Title level={5}>{t('quickLinks')}</Title>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate('/fin/credit-notes')}>
          {t('allCreditNotes')}
        </Button>
        {invId && (
          <Button type="link" block style={{ textAlign: 'left', paddingLeft: 0 }} onClick={() => navigate(`/growth/invoices/${invId}`)}>
            {t('originalTaxInvoice')}
          </Button>
        )}
        <Button
          type="link"
          block
          style={{ textAlign: 'left', paddingLeft: 0 }}
          onClick={() => navigate('/compliance/uae-einvoicing')}
        >
          {t('uaeEinvoicingRegister')}
        </Button>
      </Space>
      {latestTx && (
        <>
          <Title level={5} style={{ marginTop: 16 }}>
            {t('eInvoice')}
          </Title>
          <Text>
            {t('status')}: <Tag>{latestTx.status}</Tag>
          </Text>
          {latestTx.provider_entry_id && (
            <div>
              <Text type="secondary">{t('eInvoiceEntry', { id: latestTx.provider_entry_id })}</Text>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div style={{ margin: '-20px -20px 0' }}>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel={t('customerCreditNoteDocument')}
            documentNumber={cn.credit_note_number}
            statusTag={<Tag color={isPosted ? 'green' : cn.status === 'Cancelled' ? 'red' : 'default'}>{statusLabel(cn.status)}</Tag>}
            subtitle={
              <Text type="secondary">{cn.credit_note_date ? dayjs(cn.credit_note_date).format('DD MMM YYYY') : ''}</Text>
            }
            facts={
              <Space wrap>
                <Text>
                  <strong>{t('customer')}:</strong>{' '}
                  {(cn as { customer?: { customer_name?: string } }).customer?.customer_name ??
                    cn.customer_id ??
                    '—'}
                </Text>
                <Text>
                  <strong>{t('amount')}:</strong> {formatCurrency(Number(cn.total_amount))}
                </Text>
              </Space>
            }
            primaryActions={headerActions}
          />
        }
        workflow={<WorkflowStrip stages={ccnWorkflowStages} current={ccnWorkflowCurrent} compact />}
        rightRail={rightRail}
      >
        <Card bordered={false} styles={{ body: { padding: 0 } }}>
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small" bordered>
            <Descriptions.Item label={t('customer')}>
              {(cn as { customer?: { customer_name?: string } }).customer?.customer_name ??
                cn.customer_id}
            </Descriptions.Item>
            <Descriptions.Item label={t('creditNoteDate')}>
              {cn.credit_note_date ? dayjs(cn.credit_note_date).format('DD MMM YYYY') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('totalAmount')}>
              {formatCurrency(Number(cn.total_amount))}
            </Descriptions.Item>
            <Descriptions.Item label={t('reason')} span={3}>
              {cn.reason ?? '-'}
            </Descriptions.Item>
            {cn.original_sales_invoice_id && (
              <Descriptions.Item label={t('originalInvoice')}>
                {(cn as { originalSalesInvoice?: { invoice_number?: string } }).originalSalesInvoice
                  ?.invoice_number ?? cn.original_sales_invoice_id}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
        <div style={{ marginTop: 16 }}>
          <EntityAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_CUSTOMER_CREDIT_NOTE}
            entityId={cn.id}
            title={tc('attachments')}
            readOnly={cn.status === 'Cancelled'}
            queryKeyPrefix={['documents', 'customer-credit-note']}
            uploadHint={SCM_ATTACHMENT_UPLOAD_HINT}
          />
          <InheritedAttachmentsCard
            trailDomain="sales"
            entityType={DOCUMENT_ENTITY_TYPE_CUSTOMER_CREDIT_NOTE}
            entityId={cn.id}
            queryKeyPrefix={['documents', 'customer-credit-note']}
          />
        </div>
      </WorkspaceLayout>
    </div>
  );
};

export default CustomerCreditNoteDetailPage;
